import { PROVIDER_CATALOG, type Hosted, type ProviderView } from '@cyberscore/contracts';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  CheckCircle2,
  CircleAlert,
  CircleDashed,
  Columns3,
  Download,
  Filter,
  Info,
  Layers,
  LayoutGrid,
  Loader2,
  Pencil,
  Plug,
  Plus,
  Rows3,
  Search,
  Star,
  Table2,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { Menu } from '../../ui/Menu';
import { Dialog } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { useCheckProvider, useDeleteProvider, useProviders, useUpdateProvider } from './api';
import { ProviderForm } from './ProviderForm';
import {
  activeFilterCount,
  COLUMNS,
  DEFAULT_COLUMNS,
  FILTER_GROUPS,
  filterRows,
  GROUP_LABEL,
  groupRows,
  HOSTED_LABEL,
  paginate,
  sortRows,
  toCsv,
  type ColumnId,
  type FilterKey,
  type Filters,
  type GroupBy,
  type PageSize,
  type Sort,
} from './table';

interface Prefs {
  columns: ColumnId[];
  density: 'comfortable' | 'compact';
  view: 'table' | 'cards';
  pageSize: PageSize;
}
const PREFS_KEY = 'cyberscore.providers.view';
const DEFAULT_PREFS: Prefs = {
  columns: DEFAULT_COLUMNS,
  density: 'comfortable',
  view: 'table',
  pageSize: 10,
};
const SORTABLE: ColumnId[] = COLUMNS.filter((c) => c.sort).map((c) => c.id);
const FILTER_FOR: Partial<Record<ColumnId, FilterKey>> = {
  provider: 'kind',
  kind: 'kind',
  hosted: 'hosted',
  status: 'status',
  enabled: 'enabled',
};

/** `embedded`: inside Tenant 360's tab (task 102), under a smaller heading. */
export function ProvidersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { tenant } = useSession();
  const { data, isLoading, error } = useProviders();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<Sort | null>(null);
  const [group, setGroup] = useState<GroupBy>('none');
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [editing, setEditing] = useState<ProviderView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ProviderView | null>(null);
  const canEdit = can(tenant, 'ai_providers.create', 'ai_providers.edit', 'ai_providers.delete');

  useEffect(() => savePrefs(prefs), [prefs]);
  // Back to page 1 whenever the search, filters or page size change.
  const query = JSON.stringify([search, filters, prefs.pageSize]);
  const [pageAt, setPageAt] = useState({ query, page: 1 });
  const page = pageAt.query === query ? pageAt.page : 1;
  const setPage = (p: number) => setPageAt({ query, page: p });

  const all = useMemo(() => data ?? [], [data]);
  const visible = useMemo(
    () => sortRows(filterRows(all, search, filters), sort),
    [all, search, filters, sort],
  );
  const paged = paginate(visible, page, prefs.pageSize);
  const groups = groupRows(paged.rows, group);
  const cols = COLUMNS.filter((c) => prefs.columns.includes(c.id));
  const nFilters = activeFilterCount(filters);

  const toggleFilter = (key: FilterKey, value: string) =>
    setFilters((f) => {
      const cur = f[key] ?? [];
      return { ...f, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  const sortBy = (column: ColumnId) =>
    setSort((s) =>
      s?.column !== column ? { column, dir: 'asc' } : s.dir === 'asc' ? { column, dir: 'desc' } : null,
    );
  const exportCsv = () => {
    const blob = new Blob([toCsv(visible, prefs.columns)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `llm-providers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (tenant && tenant.kind !== 'platform') {
    return (
      <div className="card empty">
        <strong>Model providers are managed by the platform</strong>
        Switch to the platform tenant to add or change them.
      </div>
    );
  }

  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>AI providers</h2> : <h1>AI providers</h1>}
        <p>Model APIs the agents use. Every tenant’s assessment chat runs on the default provider.</p>
      </div>
      <div className={`card${prefs.density === 'compact' ? ' compact' : ''}`}>
        <div className="card-head">
          <div className="card-title">
            Cloud LLM Providers
            <span
              className="badge"
              title="API keys are encrypted at rest and never shown again. A saved key is only sent to the endpoint it was entered for."
              aria-label="About provider keys"
            >
              <Info size={13} />
            </span>
          </div>
          {canEdit && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              <Plus size={17} /> Add provider
            </button>
          )}
        </div>

        <div className="toolbar">
          <label className="search">
            <Search size={15} aria-hidden />
            <span className="sr-only">Search providers</span>
            <input
              type="search"
              placeholder="Search providers"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <Menu
            label="Filter"
            icon={<Filter size={15} />}
            count={nFilters || undefined}
            active={nFilters > 0}
          >
            {() => (
              <>
                {FILTER_GROUPS.map((g) => (
                  <div key={g.key}>
                    <div className="menu-label">{g.label}</div>
                    {g.options.map((o) => {
                      const on = !!filters[g.key]?.includes(o.value);
                      return (
                        <button
                          key={o.value}
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={on}
                          className="menu-item"
                          onClick={() => toggleFilter(g.key, o.value)}
                        >
                          <span className="check">{on && <Check size={15} />}</span>
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
                <div className="menu-sep" />
                <div className="menu-foot">
                  <button type="button" className="linklike" onClick={() => setFilters({})}>
                    Clear filters
                  </button>
                </div>
              </>
            )}
          </Menu>

          <Menu label="Sort" icon={<ArrowUpDown size={15} />} active={!!sort}>
            {(close) => (
              <>
                <div className="menu-label">Sort by</div>
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    setSort(null);
                    close();
                  }}
                >
                  <span className="check">{!sort && <Check size={15} />}</span>Default first, then name
                </button>
                {COLUMNS.filter((c) => SORTABLE.includes(c.id)).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="menu-item"
                    onClick={() => setSort({ column: c.id, dir: sort?.column === c.id ? sort.dir : 'asc' })}
                  >
                    <span className="check">{sort?.column === c.id && <Check size={15} />}</span>
                    {c.label}
                  </button>
                ))}
                {sort && (
                  <>
                    <div className="menu-sep" />
                    <div className="menu-foot">
                      <button
                        type="button"
                        className="linklike"
                        onClick={() => setSort({ ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
                      >
                        {sort.dir === 'asc' ? 'Ascending' : 'Descending'} · switch
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </Menu>

          <Menu label="Group" icon={<Layers size={15} />} active={group !== 'none'}>
            {(close) =>
              (Object.keys(GROUP_LABEL) as GroupBy[]).map((g) => (
                <button
                  key={g}
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    setGroup(g);
                    close();
                  }}
                >
                  <span className="check">{group === g && <Check size={15} />}</span>
                  {GROUP_LABEL[g]}
                </button>
              ))
            }
          </Menu>

          <Menu
            label="Columns"
            icon={<Columns3 size={15} />}
            count={`${prefs.columns.length + 1}/${COLUMNS.length + 1}`}
          >
            {() => (
              <>
                <div className="menu-label">Show columns</div>
                {COLUMNS.map((c) => {
                  const on = prefs.columns.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={on}
                      className="menu-item"
                      disabled={c.id === 'provider'}
                      onClick={() =>
                        setPrefs((p) => ({
                          ...p,
                          columns: on
                            ? p.columns.filter((x) => x !== c.id)
                            : COLUMNS.map((x) => x.id).filter((x) => x === c.id || p.columns.includes(x)),
                        }))
                      }
                    >
                      <span className="check">{on && <Check size={15} />}</span>
                      {c.label}
                    </button>
                  );
                })}
                <div className="menu-sep" />
                <div className="menu-foot">
                  <button
                    type="button"
                    className="linklike"
                    onClick={() => setPrefs((p) => ({ ...p, columns: DEFAULT_COLUMNS }))}
                  >
                    Reset columns
                  </button>
                </div>
              </>
            )}
          </Menu>

          <button
            type="button"
            className="btn"
            onClick={() =>
              setPrefs((p) => ({ ...p, density: p.density === 'comfortable' ? 'compact' : 'comfortable' }))
            }
            aria-label={`Row density: ${prefs.density}. Switch.`}
          >
            <Rows3 size={15} />
            {prefs.density === 'comfortable' ? 'Comfortable' : 'Compact'}
          </button>

          <div className="seg" role="group" aria-label="View">
            <button
              type="button"
              aria-pressed={prefs.view === 'table'}
              aria-label="Table view"
              onClick={() => setPrefs((p) => ({ ...p, view: 'table' }))}
            >
              <Table2 size={16} />
            </button>
            <button
              type="button"
              aria-pressed={prefs.view === 'cards'}
              aria-label="Card view"
              onClick={() => setPrefs((p) => ({ ...p, view: 'cards' }))}
            >
              <LayoutGrid size={16} />
            </button>
          </div>

          <span className="grow" />
          <button type="button" className="btn" onClick={exportCsv} disabled={!visible.length}>
            <Download size={15} /> CSV
          </button>
        </div>

        {isLoading ? (
          <div className="empty">
            <Loader2 size={20} className="spin" />
          </div>
        ) : error ? (
          <div className="empty">
            <strong>Couldn’t load providers</strong>
            {error instanceof ApiError ? error.message : 'Try again.'}
          </div>
        ) : !all.length ? (
          <div className="empty" style={{ borderTop: '1px solid var(--border)' }}>
            <strong>No providers yet</strong>
            Add Together AI, OpenAI, Claude, Gemini or your own OpenAI-compatible server. Until then the agent
            service uses its own settings.
            {canEdit && (
              <div style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
                  <Plus size={17} /> Add provider
                </button>
              </div>
            )}
          </div>
        ) : !visible.length ? (
          <div className="empty" style={{ borderTop: '1px solid var(--border)' }}>
            <strong>No providers match</strong>
            <button
              type="button"
              className="linklike"
              onClick={() => {
                setSearch('');
                setFilters({});
              }}
            >
              Clear the search and filters
            </button>
          </div>
        ) : prefs.view === 'table' ? (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c.id} className={c.align} scope="col" aria-sort={ariaSort(sort, c.id)}>
                      <span className="th-inner">
                        {c.label}
                        {c.sort && (
                          <button
                            type="button"
                            className={`th-btn${sort?.column === c.id ? ' on' : ''}`}
                            aria-label={`Sort by ${c.label}`}
                            onClick={() => sortBy(c.id)}
                          >
                            {sort?.column === c.id ? (
                              sort.dir === 'asc' ? (
                                <ArrowUp size={13} />
                              ) : (
                                <ArrowDown size={13} />
                              )
                            ) : (
                              <ArrowUpDown size={13} />
                            )}
                          </button>
                        )}
                        {FILTER_FOR[c.id] && (
                          <ColumnFilter
                            filterKey={FILTER_FOR[c.id]!}
                            filters={filters}
                            onToggle={toggleFilter}
                          />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="right" scope="col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <GroupRows key={g.label ?? 'all'} label={g.label} span={cols.length + 1}>
                    {g.rows.map((p) => (
                      <tr key={p.id}>
                        {cols.map((c) => (
                          <td key={c.id} className={c.align}>
                            <Cell provider={p} column={c.id} canEdit={canEdit} />
                          </td>
                        ))}
                        <td className="right">
                          <RowActions
                            provider={p}
                            canEdit={canEdit}
                            onEdit={() => setEditing(p)}
                            onDelete={() => setDeleting(p)}
                          />
                        </td>
                      </tr>
                    ))}
                  </GroupRows>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="cards">
            {paged.rows.map((p) => (
              <div className="pcard" key={p.id}>
                <Cell provider={p} column="provider" canEdit={canEdit} />
                <dl>
                  <dt>Endpoint</dt>
                  <dd className="mono">{p.endpoint}</dd>
                  <dt>Model</dt>
                  <dd className="mono">{p.model}</dd>
                  <dt>API key</dt>
                  <dd className="mono">{p.key_hint ?? '—'}</dd>
                  <dt>Hosted</dt>
                  <dd>
                    <Cell provider={p} column="hosted" canEdit={canEdit} />
                  </dd>
                </dl>
                <Cell provider={p} column="status" canEdit={canEdit} />
                <RowActions
                  provider={p}
                  canEdit={canEdit}
                  onEdit={() => setEditing(p)}
                  onDelete={() => setDeleting(p)}
                />
              </div>
            ))}
          </div>
        )}

        {!!all.length && (
          <div className="foot">
            <span>
              Showing {paged.from}–{paged.to} of {paged.total} provider{paged.total === 1 ? '' : 's'}
              {paged.total !== all.length && ` (${all.length} in all)`}
            </span>
            <span>View</span>
            <div className="pager" role="group" aria-label="Rows per page">
              {([10, 25, 50, 'all'] as PageSize[]).map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={prefs.pageSize === n}
                  onClick={() => setPrefs((p) => ({ ...p, pageSize: n }))}
                >
                  {n === 'all' ? 'All' : n}
                </button>
              ))}
            </div>
            {paged.pages > 1 && (
              <div className="pages">
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={paged.page <= 1}
                  onClick={() => setPage(paged.page - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {paged.page} of {paged.pages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={paged.page >= paged.pages}
                  onClick={() => setPage(paged.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {editing && (
        <ProviderForm
          provider={editing === 'new' ? undefined : editing}
          isFirst={!all.length}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && <DeleteDialog provider={deleting} onClose={() => setDeleting(null)} />}
    </>
  );
}

function GroupRows({ label, span, children }: { label: string | null; span: number; children: ReactNode }) {
  return (
    <>
      {label && (
        <tr className="group">
          <td colSpan={span}>{label}</td>
        </tr>
      )}
      {children}
    </>
  );
}

function ColumnFilter({
  filterKey,
  filters,
  onToggle,
}: {
  filterKey: FilterKey;
  filters: Filters;
  onToggle: (k: FilterKey, v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const group = FILTER_GROUPS.find((g) => g.key === filterKey)!;
  const on = !!filters[filterKey]?.length;
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !(e.target as Element).closest?.('.colfilter') && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <span className="menu-wrap colfilter">
      <button
        type="button"
        className={`th-btn${on ? ' on' : ''}`}
        aria-label={`Filter by ${group.label}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Filter size={12} />
      </button>
      {open && (
        <div
          className="menu"
          role="menu"
          style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}
        >
          <div className="menu-label">{group.label}</div>
          {group.options.map((o) => {
            const checked = !!filters[filterKey]?.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                className="menu-item"
                onClick={() => onToggle(filterKey, o.value)}
              >
                <span className="check">{checked && <Check size={15} />}</span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

function Cell({
  provider: p,
  column,
  canEdit,
}: {
  provider: ProviderView;
  column: ColumnId;
  canEdit: boolean;
}) {
  const update = useUpdateProvider();
  const toast = useToast();
  switch (column) {
    case 'provider':
      return (
        <div>
          <div className="cell-title">
            {p.name}
            {p.is_default && (
              <span className="badge" title="Used by the assessment chat for every tenant">
                <Star size={12} /> Default
              </span>
            )}
          </div>
          <div className="cell-sub">{PROVIDER_CATALOG[p.kind].label}</div>
        </div>
      );
    case 'endpoint_model':
      return (
        <div>
          <div className="mono clip" title={p.endpoint}>
            {p.endpoint}
          </div>
          <div className="cell-sub mono clip" title={p.model}>
            {p.model}
          </div>
        </div>
      );
    case 'api_key':
      return <span className="mono muted">{p.key_hint ?? 'None'}</span>;
    case 'hosted':
      return (
        <select
          className={`hosted-select ${p.hosted}`}
          value={p.hosted}
          disabled={!canEdit || update.isPending}
          aria-label={`Hosting for ${p.name}`}
          onChange={(e) =>
            update.mutate(
              { id: p.id, revision: p.revision, hosted: e.target.value as Hosted },
              {
                onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not save', 'bad'),
              },
            )
          }
        >
          {(Object.keys(HOSTED_LABEL) as Hosted[]).map((h) => (
            <option key={h} value={h}>
              {HOSTED_LABEL[h]}
            </option>
          ))}
        </select>
      );
    case 'status':
      return <Status provider={p} />;
    default: {
      const col = COLUMNS.find((c) => c.id === column)!;
      const mono = column === 'endpoint' || column === 'model';
      return <span className={mono ? 'mono' : undefined}>{col.text(p)}</span>;
    }
  }
}

function Status({ provider: p }: { provider: ProviderView }) {
  if (!p.enabled) {
    return (
      <div>
        <span className="pill neutral">Disabled</span>
        <div className="status-msg">Kept, but never used.</div>
      </div>
    );
  }
  if (p.status === 'untested') {
    return (
      <div>
        <span className="pill neutral">
          <CircleDashed size={13} /> Not tested
        </span>
        <div className="status-msg">Run Test to check the connection.</div>
      </div>
    );
  }
  const ok = p.status === 'connected';
  return (
    <div>
      <span className={`pill ${ok ? 'ok' : 'bad'}`}>
        {ok ? <CheckCircle2 size={13} /> : <CircleAlert size={13} />}
        {ok ? 'Connected' : 'Failed'}
      </span>
      <div className="status-msg">{p.last_check?.message}</div>
    </div>
  );
}

function RowActions({
  provider: p,
  canEdit,
  onEdit,
  onDelete,
}: {
  provider: ProviderView;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const check = useCheckProvider();
  const toast = useToast();
  if (!canEdit) return null;
  return (
    <div className="cell-actions">
      <button
        type="button"
        className="btn btn-sm"
        disabled={check.isPending}
        onClick={() =>
          check.mutate(p.id, {
            onSuccess: ({ result }) => toast(result.message, result.ok ? 'ok' : 'bad'),
            onError: (err) => toast(err instanceof ApiError ? err.message : 'The check could not run', 'bad'),
          })
        }
      >
        {check.isPending ? <Loader2 size={14} className="spin" /> : <Plug size={14} />} Test
      </button>
      <button type="button" className="btn btn-sm" onClick={onEdit}>
        <Pencil size={14} /> Edit
      </button>
      <button type="button" className="btn btn-sm btn-danger" onClick={onDelete}>
        <Trash2 size={14} /> Delete
      </button>
    </div>
  );
}

function DeleteDialog({ provider, onClose }: { provider: ProviderView; onClose: () => void }) {
  const del = useDeleteProvider();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      title={`Delete ${provider.name}?`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger-solid"
            disabled={del.isPending}
            onClick={() =>
              del.mutate(
                { id: provider.id, revision: provider.revision },
                {
                  onSuccess: () => {
                    toast(`${provider.name} deleted.`);
                    onClose();
                  },
                  onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not delete'),
                },
              )
            }
          >
            {del.isPending && <Loader2 size={14} className="spin" />}
            Delete provider
          </button>
        </>
      }
    >
      <p>
        Its encrypted API key is deleted with it. Past AI usage records keep the provider’s name.
        {provider.is_default && ' This is the default provider: make another one the default first.'}
      </p>
      {error && (
        <div className="result bad" role="alert" style={{ marginBottom: 14 }}>
          <CircleAlert size={16} />
          <span>{error}</span>
        </div>
      )}
    </Dialog>
  );
}

function ariaSort(sort: Sort | null, id: ColumnId) {
  if (sort?.column !== id) return undefined;
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = { ...DEFAULT_PREFS, ...JSON.parse(raw) } as Prefs;
    const known = new Set(COLUMNS.map((c) => c.id));
    p.columns = p.columns.filter((c) => known.has(c));
    if (!p.columns.includes('provider')) p.columns = ['provider', ...p.columns];
    return p;
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(p: Prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable: preferences last for this visit only */
  }
}
